import { Checkbox, Input, Modal } from "antd";
import { useFormik } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";

import { useUpdateIncome } from "../../api/api.update";
import { useGetGroupUsers2 } from "../../api/api.get";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { IIncome } from "../../types/types";
import { capitalize } from "../../utils/functions";

export default function EditIncomeDialog({
  open,
  income,
  onCancel,
  onSuccess,
  onError,
}: {
  open: boolean;
  income?: IIncome;
  onCancel: () => void;
  onSuccess: () => void;
  onError: () => void;
}) {
  const { user } = useAuthContext();
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();

  const { data: groupUsers } = useGetGroupUsers2(defaultGroupId);
  const { mutateAsync, isPending } = useUpdateIncome(defaultGroupId);

  const formik = useFormik({
    initialValues: {
      name: income?.name ?? "",
      amount: income?.amount ?? 0,
      usernames: income?.users ?? [],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string(),
      amount: Yup.number()
        .typeError("Please enter valid number")
        .positive("Amount must be greater than zero")
        .required("Please enter amount"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        incomeId: income!.id,
        name: values.name,
        amount: +values.amount,
        users: values.usernames,
      })
        .then(() => onSuccess())
        .catch(() => onError());
    },
  });

  const userOptions = useMemo(() => {
    if (!groupUsers) return [];
    return groupUsers
      .filter((gu) => gu.username !== user?.username)
      .map((gu) => ({
        label: capitalize(gu.firstName || gu.username),
        value: gu.username,
      }));
  }, [groupUsers, user]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Modal
        title="Edit Income"
        open={open}
        onOk={formik.submitForm}
        onCancel={onCancel}
        okText="Save"
        okButtonProps={{ loading: isPending }}
      >
        <div className="flex flex-col gap-6 my-6">
          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <Input
              placeholder="e.g., Monthly salary"
              {...formik.getFieldProps("name")}
              status={formik.errors.name ? "error" : undefined}
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Annual Income (pre-tax) *
            </label>
            <Input
              type="number"
              prefix={currency}
              placeholder="e.g., 60000"
              {...formik.getFieldProps("amount")}
              status={formik.errors.amount ? "error" : undefined}
            />
            {formik.errors.amount && (
              <p className="text-xs mt-1 text-red-500">
                {formik.errors.amount}
              </p>
            )}
          </div>

          {/* Share Users */}
          <div>
            <p className="text-sm font-semibold mb-3">Share With?</p>
            {userOptions.length > 0 ? (
              <Checkbox.Group
                options={userOptions}
                value={formik.values.usernames}
                onChange={(values) => formik.setFieldValue("usernames", values)}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                No other members in this group to share with.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </form>
  );
}
